import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { sortDatesDesc } from "../../../functions/sorting/sortDataByDate";

export const exportVisitReport = async (
  visitData = [],
  fileName = "Visits_Report",
) => {
  if (!visitData.length) {
    alert("No data available to export");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Visits");

  // Find maximum number of complaint & regular visits
  const maxComplaintVisits = Math.max(
    ...visitData.map((item) => item.complaintVisitDates?.length || 0),
    1,
  );

  const maxRegularVisits = Math.max(
    ...visitData.map((item) => item.regularVisitDates?.length || 0),
    1,
  );

  // Dynamic columns
  const columns = [
    {
      header: "Supervisor Name / Mobile Number",
      key: "supervisor",
      width: 30,
    },
    {
      header: "Field Technician Name / Mobile Number",
      key: "technician",
      width: 30,
    },
    {
      header: "Site Name",
      key: "siteName",
      width: 25,
    },
    {
      header: "Site ID",
      key: "siteId",
      width: 12,
    },
  ];

  // Complaint Visit Columns
  for (let i = 1; i <= maxComplaintVisits; i++) {
    columns.push({
      header: "Complaint Visit Date",
      key: `complaint${i}`,
      width: 15,
    });
  }

  columns.push({
    header: "Total Complaint Visits",
    key: "complaintTotal",
    width: 18,
  });

  // Regular Visit Columns
  for (let i = 1; i <= maxRegularVisits; i++) {
    columns.push({
      header: "Regular Visit Date",
      key: `regular${i}`,
      width: 15,
    });
  }

  columns.push({
    header: "Total Regular Visits",
    key: "regularTotal",
    width: 18,
  });

  columns.push({
    header: "Total Number of Visits",
    key: "totalVisits",
    width: 20,
  });

  worksheet.columns = columns;

  // Header Styling
  const headerRow = worksheet.getRow(1);

  headerRow.height = 35;

  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      size: 11,
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };

    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Group by Supervisor + Technician
  const groupedData = Object.values(
    visitData.reduce((acc, item) => {
      const key = `${item.supervisorID}_${item.fieldTechnicianID}`;

      if (!acc[key]) {
        acc[key] = {
          supervisor: `${item.supervisorName}\n(${item.supervisorMobileNo})`,
          technician: `${item.fieldTechnicianName}\n(${item.fieldTechnicianNo})`,
          sites: [],
        };
      }

      acc[key].sites.push(item);

      return acc;
    }, {}),
  );

  groupedData.forEach((group) => {
    group.sites.forEach((site) => {
      const complaintDates = sortDatesDesc(site.complaintVisitDates || []);
      const regularDates = sortDatesDesc(site.regularVisitDates || []);

      const rowData = [
        `${site.supervisorName}\n(${site.supervisorMobileNo})`,
        `${site.fieldTechnicianName}\n(${site.fieldTechnicianNo})`,
        site.stationName?.trim() || "",
        site.stationID || "",
      ];

      // Dynamic Complaint Dates
      for (let i = 0; i < maxComplaintVisits; i++) {
        rowData.push(complaintDates[i] || "");
      }

      rowData.push(complaintDates.length);

      // Dynamic Regular Dates
      for (let i = 0; i < maxRegularVisits; i++) {
        rowData.push(regularDates[i] || "");
      }

      rowData.push(regularDates.length);

      // Total Number of Visits
      rowData.push(complaintDates.length + regularDates.length);

      worksheet.addRow(rowData);
    });
  });

  // Apply Borders & Alignment
  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    });
  });

  // Freeze Header
  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`,
  );
};
